<template>
    <base-card
        :key="artist.id"
        :clickable="true"
        :selectable="selectable"
        :clear="clear"
        :image="getArtistImage"
        :cta="{
            text: cta,
            disabled: false
        }"
        :show-cta="showCta"
        :styles="{
            card: {
                position: 'relative',
                maxWidth: maxWidth,
                ...cardStyles
            }
        }"
        @cta-clicked="onCardClicked"
        @card-clicked="onCardClicked"
    >
        <template #music-player>
            <music-player
                v-slot="{ player, controls }"
                :mk="`search-result-${artist.slug}-${artist.id}`"
                :songs.sync="artist.audios"
                :styles="{
                    container: {
                        flexDirection: 'row',
                        gap: '50px'
                    }
                }"
                @sync-title-change="() => {}"
                @sync-song-delete="() => {}"
                mode="custom"
            >
                <button
                    v-if="!player[0].playing"
                    @click="controls.handlePlay(player[0], 0)"
                    class="play-btn"
                >
                    <img :src="URLS.icons.play" class="play-icon" alt="play-btn" />
                </button>
                <button v-else @click="controls.handlePause(player[0])" class="play-btn">
                    <img :src="URLS.icons.play" class="play-icon" alt="play-icon" />
                </button>
            </music-player>
        </template>
        <template #content-main>
            <div class="artist-info">
                <p class="band-name">{{ artist.band_name }}</p>
                <p class="band-location">
                    {{ artist.city_state_address }}
                </p>
            </div>
            <p class="vibes">{{ artistVibes }}</p>
            <div v-show="artist.artist_stats" class="socials">
                <div v-for="social in artistSocials" :key="social.id" class="social">
                    <img :src="social.icon" alt="social-icon" class="social-icon" />
                    <p class="followers">{{ social.followers }}</p>
                </div>
            </div>
        </template>
        <template #footer-text>
            <p v-if="artist.min_payout" class="footer-text">
                Starting at ${{ parseFloat(artist.min_payout).toFixed(0) }}
            </p>
        </template>
    </base-card>
</template>
<script>
    import BaseCard from '@/components/ui/cards/BaseCard.vue'
    import MusicPlayer from '@/components/ui/music-player/MusicPlayer.vue'
    import useFormattedData from '@/composables/useFormattedData'

    const URLS = {
        redirect: {
            artist: '/artists'
        },
        icons: {
            instagram: '/images/icons/instagram-grey.svg',
            tiktok: '/images/icons/tiktok-grey.svg',
            youtube: '/images/icons/youtube-grey.svg',
            play: '/images/icons/video-play.svg'
        }
    }
    export default {
        name: 'Artist',
        components: {
            BaseCard,
            MusicPlayer
        },
        props: {
            artist: {
                type: Object,
                required: true
            },
            maxWidth: {
                type: String,
                required: false,
                default: '336px'
            },
            cta: {
                type: String,
                required: false,
                default: 'Book Artist'
            },
            showCta: {
                type: Boolean,
                required: false,
                default: true
            },
            selectable: {
                type: Boolean,
                required: false,
                default: false
            },
            clear: {
                type: Boolean,
                required: false,
                default: false
            },
            redirect: {
                type: Boolean,
                required: false,
                default: true
            },
            cardStyles: {
                type: Object,
                required: false,
                default: () => ({})
            }
        },
        emits: ['book-artist'],
        setup() {
            const { formatBigNumbers } = useFormattedData()
            return {
                formatBigNumbers
            }
        },
        computed: {
            URLS: () => URLS,
            getArtistImage() {
                return this.artist.avatar
            },
            artistSocials() {
                if (!this.artist.artist_stats) {
                    return []
                }

                return Object.keys(this.artist.artist_stats)
                    .filter((key) =>
                        ['instagramStat', 'tiktokStat', 'youtubeStat'].includes(key)
                    )
                    .map((key, index) => ({
                        id: index,
                        icon: this.URLS.icons[key.replace('Stat', '')],
                        followers: this.formatBigNumbers(
                            this.artist.artist_stats[key]?.[0]?.followers || 0
                        )
                    }))
            },
            artistVibes() {
                if (!this.artist.keyword || typeof this.artist.keyword !== 'string') {
                    return ''
                }

                const keywords = this.artist.keyword
                    .split(',')
                    .map((keyword) => keyword.trim())
                    .map(
                        (keyword) =>
                            keyword.charAt(0).toUpperCase() + keyword.slice(1).toLowerCase()
                    )

                if (keywords.length > 3) {
                    return `${keywords.slice(0, 3).join(', ')} and ${
                        keywords.length - 3
                    } more...`
                }

                return keywords.join(', ')
            }
        },
        methods: {
            onCardClicked() {
                if (this.redirect) {
                    window.open(`${this.URLS.redirect.artist}/${this.artist.slug}`, '_blank')
                } else {
                    this.$emit('book-artist', this.artist)
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    .play-btn {
        position: absolute;
        top: 152px;
        left: 8px;
        padding: 8px;
        width: 32px;
        height: 32px;
        border-radius: 120px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: rgba(0, 0, 0, 0.6);
        .play-icon {
            width: 32px;
            height: 32px;
            object-fit: cover;
        }
    }
    .artist-info {
        width: 100%;

        .band-name {
            color: #dedede;
            font-variant-numeric: ordinal;
            font-family: 'Instrument Sans';
            font-size: 20px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
        .band-location {
            color: #dedede;
            font-variant-numeric: ordinal;
            font-family: 'Instrument Sans';
            font-size: 12px;
            font-style: normal;
            font-weight: 500;
            line-height: 16px;
        }
    }
    .vibes {
        overflow: hidden;
        text-wrap: nowrap;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        width: 100%;
        color: #8b8b8b;
        font-variant-numeric: ordinal;
        text-overflow: ellipsis;
        font-family: 'Instrument Sans';
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
    }
    .socials,
    .social {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
    }
    .socials {
        width: 100%;
        height: 16px;
        overflow: auto;
        gap: 16px;

        .social {
            gap: 4px;

            .social-icon {
                width: 16px;
                height: 16px;
                object-fit: contain;
            }
            .followers {
                color: #8b8b8b;
                font-family: 'Instrument Sans';
                font-size: 12px;
                font-style: normal;
                font-weight: 500;
                line-height: 16px;
            }
        }
    }
    .footer-text {
        color: #acacac;
        font-variant-numeric: ordinal;
        font-family: 'Instrument Sans';
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px;
    }
</style>
